<template>
  <div class="assistant-chat" :class="{ 'assistant-chat--roomy': roomy }">
    <div ref="scrollEl" class="assistant-chat__body">
      <!-- Welcome -->
      <div v-if="messages.length === 0 && !isLoadingThread" class="assistant-chat__welcome">
        <div class="assistant-chat__orb" aria-hidden="true">
          <Icon icon="mdi:creation" />
        </div>
        <h3 class="assistant-chat__hello">
          {{ firstName ? `Hi ${firstName}, how can I help?` : "How can I help?" }}
        </h3>
        <p class="assistant-chat__intro">
          Ask about your contacts, deals and meetings, or have me draft emails, LinkedIn messages
          and meeting invites. Nothing is sent until you approve it.
        </p>
        <div class="assistant-chat__suggestions">
          <button
            v-for="suggestion in SUGGESTIONS"
            :key="suggestion.text"
            type="button"
            class="assistant-chat__suggestion"
            :disabled="isSending"
            @click="send(suggestion.text)"
          >
            <span class="assistant-chat__suggestion-icon">
              <Icon :icon="suggestion.icon" />
            </span>
            <span class="assistant-chat__suggestion-text">{{ suggestion.text }}</span>
            <Icon icon="mdi:arrow-top-right" class="assistant-chat__suggestion-go" />
          </button>
        </div>
      </div>

      <!-- A reply thread: who it's about -->
      <div v-if="replyThread?.contact" class="assistant-chat__contact">
        <ContactAvatar
          :name="replyThread.contact.name"
          :src="replyThread.contact.profilePictureUrl"
          :size="44"
        />
        <div class="assistant-chat__contact-text">
          <RouterLink
            :to="{ name: CONTACT_OVERVIEW_ROUTE, params: { id: replyThread.contact.id } }"
            class="assistant-chat__contact-name"
          >
            {{ replyThread.contact.name }}
          </RouterLink>
          <p v-if="contactRole" class="assistant-chat__contact-role">{{ contactRole }}</p>
        </div>
      </div>

      <!-- Conversation -->
      <template v-for="msg in messages" :key="msg.id">
        <div class="assistant-chat__row" :class="`assistant-chat__row--${msg.role}`">
          <span v-if="msg.role !== 'user'" class="assistant-chat__avatar" aria-hidden="true">
            <Icon :icon="msg.role === 'error' ? 'mdi:alert-circle-outline' : 'mdi:creation'" />
          </span>
          <div class="assistant-chat__stack">
            <AgentSteps v-if="msg.steps?.length" :steps="msg.steps" />

            <!-- The assistant announcing a contact's reply and the answer it drafted -->
            <div
              v-if="msg.replyDraft?.inbound"
              class="assistant-chat__bubble assistant-chat__bubble--assistant assistant-chat__reply"
            >
              <p>
                {{ firstName ? `Hey ${firstName}, you` : "You" }} got a reply from
                <strong>{{ replyContactName }}</strong>
                {{ CHANNEL_LABEL[msg.replyDraft.channel] }}. {{ replyContactName }} said:
              </p>
              <div
                v-for="(inbound, i) in msg.replyDraft.inbound"
                :key="i"
                class="assistant-chat__inbound"
              >
                <ContactAvatar
                  :name="replyContactName"
                  :src="replyThread?.contact?.profilePictureUrl"
                  :size="24"
                />
                <div class="assistant-chat__inbound-bubble">
                  <p class="assistant-chat__inbound-text">{{ inbound.text }}</p>
                  <time class="assistant-chat__inbound-time" :datetime="inbound.receivedAt">
                    {{ formatTime(inbound.receivedAt) }}
                  </time>
                </div>
              </div>
              <p>Here's the reply I prepared for you:</p>
            </div>

            <div
              v-else
              class="assistant-chat__bubble"
              :class="`assistant-chat__bubble--${msg.role}`"
            >
              <div
                v-if="msg.role === 'assistant'"
                class="assistant-chat__markdown"
                v-html="renderMarkdown(msg.content)"
              />
              <template v-else>{{ msg.content }}</template>
            </div>

            <!-- The draft: live (Send / Edit / Dismiss) on the latest message that shows it -->
            <template v-if="msg.replyDraft">
              <AgentActionCard
                v-if="msg.id === liveDraftMessageId && replyThread?.draft"
                :action="replyThread.draft"
                :busy="busyIds.has(replyThread.draft.id)"
                :now="now"
                title="Suggested reply"
                approve-label="Send"
                reject-label="Dismiss"
                quick-reject
                @approve="approve(replyThread.draft, $event)"
                @reject="reject(replyThread.draft, $event ?? 'Dismissed')"
              />
              <div v-else class="assistant-chat__old-draft">
                <span class="assistant-chat__old-draft-label">
                  <Icon icon="mdi:history" />
                  Earlier version
                </span>
                <p v-if="msg.replyDraft.draft.subject" class="assistant-chat__old-draft-subject">
                  {{ msg.replyDraft.draft.subject }}
                </p>
                <p class="assistant-chat__old-draft-text">{{ draftPlainText(msg.replyDraft) }}</p>
              </div>
            </template>

            <div v-if="msg.actions?.length" class="assistant-chat__actions">
              <div v-if="openActions(msg).length > 1" class="assistant-chat__actions-bar">
                <span>
                  <Icon icon="mdi:hand-back-right-outline" />
                  {{ openActions(msg).length }} drafts need your approval
                </span>
                <div class="assistant-chat__actions-bulk">
                  <Button
                    variant="ghost"
                    size="sm"
                    :disabled="isDeciding(msg)"
                    @click="decideAll(openActions(msg), 'reject')"
                  >
                    Reject all
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    :disabled="isDeciding(msg)"
                    :loading="isDeciding(msg)"
                    @click="decideAll(openActions(msg), 'approve')"
                  >
                    Approve all ({{ openActions(msg).length }})
                  </Button>
                </div>
              </div>
              <AgentActionCard
                v-for="action in msg.actions"
                :key="action.id"
                :action="action"
                :busy="busyIds.has(action.id)"
                :now="now"
                @approve="approve(action, $event)"
                @reject="reject(action, $event)"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- A write the assistant wants confirmed before it runs -->
      <div v-if="pending" class="assistant-chat__confirm">
        <div class="assistant-chat__confirm-head">
          <Icon icon="mdi:shield-check-outline" />
          <span>Confirm before I go ahead</span>
        </div>
        <div v-for="(item, i) in pending.actions" :key="i" class="assistant-chat__confirm-action">
          <p class="assistant-chat__confirm-title">{{ item.title }}</p>
          <p v-if="item.summary" class="assistant-chat__confirm-summary">{{ item.summary }}</p>
          <ul v-if="item.items?.length" class="assistant-chat__confirm-items">
            <li v-for="(entry, j) in item.items" :key="j">
              <span class="assistant-chat__confirm-name">{{ entry.name }}</span>
              <span v-if="describeItem(entry)" class="assistant-chat__confirm-meta">
                {{ describeItem(entry) }}
              </span>
            </li>
          </ul>
        </div>
        <p v-if="isConfirmExpired" class="assistant-chat__confirm-expired">
          This confirmation expired. Ask again to get a fresh one.
        </p>
        <div class="assistant-chat__confirm-buttons">
          <Button variant="outline" size="sm" :disabled="isSending" @click="respond(false)">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            :disabled="isSending || isConfirmExpired"
            @click="respond(true)"
          >
            Confirm
          </Button>
        </div>
      </div>

      <!-- Working -->
      <div v-if="isSending || isLoadingThread" class="assistant-chat__row">
        <span class="assistant-chat__avatar assistant-chat__avatar--live" aria-hidden="true">
          <Icon icon="mdi:creation" />
        </span>
        <div class="assistant-chat__stack">
          <AgentSteps v-if="isSending && liveSteps.length" :steps="liveSteps" live />
          <div class="assistant-chat__typing" role="status">
            <span class="assistant-chat__dots" aria-hidden="true"><i /><i /><i /></span>
            <span>
              {{
                isThreadWorking
                  ? "Still working on this…"
                  : isLoadingThread
                    ? "Loading conversation…"
                    : "Thinking…"
              }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Composer -->
    <form class="assistant-chat__composer" @submit.prevent="submit">
      <div class="assistant-chat__input-wrap" :class="{ 'is-disabled': isLocked }">
        <textarea
          ref="inputEl"
          v-model="draft"
          class="assistant-chat__input"
          rows="1"
          maxlength="4000"
          :placeholder="
            replyThread
              ? 'Ask me to change the reply, or anything about it…'
              : 'Ask anything, or have me draft a follow-up…'
          "
          aria-label="Message the CRM assistant"
          :disabled="isLocked"
          @input="autoGrow"
          @keydown.enter.exact.prevent="submit"
        />
        <button
          type="submit"
          class="assistant-chat__send"
          aria-label="Send"
          title="Send (Enter)"
          :disabled="isLocked || !draft.trim()"
        >
          <Icon icon="mdi:arrow-up" />
        </button>
      </div>
      <p class="assistant-chat__hint">
        <Icon icon="mdi:shield-lock-outline" />
        Nothing is sent without your approval. AI can make mistakes.
      </p>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { RouterLink } from "vue-router";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Button, Icon } from "@/components/ui";
import AgentActionCard from "@/components/agent/AgentActionCard.vue";
import AgentSteps from "@/components/agent/AgentSteps.vue";
import ContactAvatar from "@/components/agent/assistant/ContactAvatar.vue";
import { useAssistantChat } from "@/composables/agent/useAssistantChat";
import { CONTACT_OVERVIEW_ROUTE } from "@/router/routes";
import type { AgentAction, AgentChatMessage, AgentStoredReplyDraft } from "@/dtos/Agent.dto";

defineProps<{
  /** The expanded (centered modal) layout: wider messages and suggestions in a grid. */
  roomy?: boolean;
}>();

const {
  messages,
  pending,
  liveSteps,
  isSending,
  isLoadingThread,
  isThreadWorking,
  send: sendMessage,
  respond,
  busyIds,
  approve,
  reject,
  decideAll,
  now,
  draft,
  replyThread,
} = useAssistantChat();

// ── AI reply draft threads ──────────────────────────────────
const CHANNEL_LABEL = { email: "by email", linkedin: "on LinkedIn" } as const;

const replyContactName = computed(() => replyThread.value?.contact?.name ?? "Your contact");

const contactRole = computed(() => {
  const contact = replyThread.value?.contact;
  if (!contact) return "";
  return [contact.jobTitle, contact.company].filter(Boolean).join(" at ");
});

// The draft's buttons sit on the latest message showing it; earlier ones keep their wording.
const liveDraftMessageId = computed(() => {
  const draftId = replyThread.value?.draft?.id;
  if (!draftId) return null;
  for (let i = messages.value.length - 1; i >= 0; i--) {
    if (messages.value[i].replyDraft?.actionId === draftId) return messages.value[i].id;
  }
  return null;
});

const draftPlainText = (replyDraft: AgentStoredReplyDraft): string => {
  const { text, body } = replyDraft.draft;
  if (body === undefined) return text ?? "";
  return body
    .replace(/<(br|\/p|\/div|\/li)[^>]*>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const formatTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

const SUGGESTIONS = [
  { icon: "mdi:account-clock-outline", text: "Who haven't I followed up with in two weeks?" },
  { icon: "mdi:calendar-week", text: "What meetings do I have this week?" },
  { icon: "mdi:email-edit-outline", text: "Draft a follow-up email to my most recent meeting" },
  { icon: "mdi:briefcase-search-outline", text: "Which open deals haven't moved this month?" },
];

const store = useStore();
const firstName = computed<string>(() => store.getters.account?.data?.firstName ?? "");

const scrollEl = ref<HTMLElement | null>(null);
const inputEl = ref<HTMLTextAreaElement | null>(null);

const isLocked = computed(() => isSending.value || isLoadingThread.value);

const renderMarkdown = (content: string): string =>
  DOMPurify.sanitize(marked.parse(content) as string);

const openActions = (msg: AgentChatMessage): AgentAction[] =>
  (msg.actions ?? []).filter(
    (a) => a.status === "pending" && (!a.expiresAt || new Date(a.expiresAt).getTime() > now.value)
  );

const isDeciding = (msg: AgentChatMessage): boolean =>
  openActions(msg).some((a) => busyIds.value.has(a.id));

const isConfirmExpired = computed(
  () => !!pending.value && new Date(pending.value.expiresAt).getTime() <= now.value
);

const describeItem = (item: Record<string, string | number | null>): string => {
  const role = [item.title, item.company].filter(Boolean).join(" at ");
  return [role, item.location].filter(Boolean).join(" · ");
};

// The message box grows with the text, up to a few lines.
const MAX_INPUT_PX = 140;
const autoGrow = () => {
  const el = inputEl.value;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_PX)}px`;
};

const send = (text: string) => {
  if (isLocked.value || !text.trim()) return;
  sendMessage(text);
};

const submit = () => {
  if (isLocked.value || !draft.value.trim()) return;
  send(draft.value);
  draft.value = "";
  nextTick(autoGrow);
};

const scrollToBottom = (behavior: ScrollBehavior) => {
  scrollEl.value?.scrollTo({ top: scrollEl.value.scrollHeight, behavior });
};

watch(
  [() => messages.value.length, () => liveSteps.value.length, pending, isSending],
  async () => {
    await nextTick();
    scrollToBottom("smooth");
  },
  { flush: "post" }
);

// Ready to type, at the latest message, whenever the window opens or changes size.
onMounted(() => {
  scrollToBottom("auto");
  autoGrow();
  inputEl.value?.focus();
});
</script>

<style scoped>
.assistant-chat {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background:
    radial-gradient(120% 60% at 100% 0%, rgb(124 58 237 / 6%) 0%, transparent 60%),
    radial-gradient(90% 50% at 0% 100%, rgb(59 130 246 / 5%) 0%, transparent 60%), #fcfcfe;
}

.assistant-chat__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  padding: 18px 16px 12px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.assistant-chat--roomy .assistant-chat__body {
  padding: 24px 28px 16px;
}

/* ── Welcome ─────────────────────────────────────────────── */
.assistant-chat__welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto 0;
  padding: 12px 4px;
  text-align: center;
  animation: assistant-rise 0.35s ease both;
}

.assistant-chat__orb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 14px;
  border-radius: 20px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
  box-shadow:
    0 10px 30px -8px rgb(99 102 241 / 55%),
    inset 0 1px 0 rgb(255 255 255 / 35%);
  color: #fff;
  font-size: 30px;
}

.assistant-chat__orb::after {
  content: "";
  position: absolute;
  inset: -6px;
  border-radius: 24px;
  background: linear-gradient(135deg, rgb(139 92 246 / 30%), rgb(59 130 246 / 20%));
  filter: blur(10px);
  z-index: -1;
}

.assistant-chat__hello {
  margin: 0 0 6px;
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.assistant-chat__intro {
  max-width: 420px;
  margin: 0 0 18px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.55;
}

.assistant-chat__suggestions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  width: 100%;
}

.assistant-chat--roomy .assistant-chat__suggestions {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-width: 640px;
}

.assistant-chat__suggestion {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #ece9f8;
  border-radius: 12px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.assistant-chat__suggestion:hover:not(:disabled) {
  border-color: #c4b5fd;
  box-shadow: 0 6px 16px -8px rgb(124 58 237 / 35%);
  transform: translateY(-1px);
}

.assistant-chat__suggestion:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.assistant-chat__suggestion-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: #f5f3ff;
  color: #7c3aed;
  font-size: 16px;
}

.assistant-chat__suggestion-text {
  flex: 1;
  min-width: 0;
  line-height: 1.35;
}

.assistant-chat__suggestion-go {
  flex-shrink: 0;
  color: #c4b5fd;
  font-size: 16px;
  transition: color 0.15s ease;
}

.assistant-chat__suggestion:hover .assistant-chat__suggestion-go {
  color: #7c3aed;
}

/* ── Messages ────────────────────────────────────────────── */
.assistant-chat__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  animation: assistant-rise 0.25s ease both;
}

.assistant-chat__row--user {
  justify-content: flex-end;
}

.assistant-chat__avatar {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: 2px;
  border-radius: 9px;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: #fff;
  font-size: 15px;
}

.assistant-chat__row--error .assistant-chat__avatar {
  background: #fee2e2;
  color: #b91c1c;
}

.assistant-chat__avatar--live {
  animation: assistant-glow 1.6s ease-in-out infinite;
}

.assistant-chat__stack {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.assistant-chat__row--user .assistant-chat__stack {
  flex: 0 1 auto;
  align-items: flex-end;
  max-width: 85%;
}

.assistant-chat__bubble {
  align-self: flex-start;
  max-width: 100%;
  padding: 10px 14px;
  border-radius: 4px 16px 16px 16px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-chat__bubble--assistant {
  border: 1px solid #eef0f4;
  background: #fff;
  box-shadow: 0 1px 2px rgb(17 24 39 / 4%);
  color: #111827;
  white-space: normal;
}

.assistant-chat__bubble--user {
  align-self: flex-end;
  border-radius: 16px 4px 16px 16px;
  background: linear-gradient(135deg, #7c3aed 0%, #6366f1 100%);
  box-shadow: 0 6px 14px -8px rgb(99 102 241 / 70%);
  color: #fff;
}

.assistant-chat__bubble--error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.assistant-chat__markdown :deep(p) {
  margin: 0 0 6px;
}

.assistant-chat__markdown :deep(p:last-child) {
  margin-bottom: 0;
}

.assistant-chat__markdown :deep(ul),
.assistant-chat__markdown :deep(ol) {
  margin: 4px 0;
  padding-left: 18px;
}

.assistant-chat__markdown :deep(a) {
  color: #6d28d9;
}

.assistant-chat__markdown :deep(code) {
  padding: 1px 5px;
  border-radius: 5px;
  background: #f3f4f6;
  font-size: 12.5px;
}

/* ── AI reply draft threads ──────────────────────────────── */
.assistant-chat__contact {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #ede9fe;
  border-radius: 14px;
  background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%);
}

.assistant-chat__contact-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.assistant-chat__contact-name {
  overflow: hidden;
  color: #111827;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assistant-chat__contact-name:hover {
  color: #6d28d9;
  text-decoration: underline;
}

.assistant-chat__contact-role {
  margin: 0;
  overflow: hidden;
  color: #6b7280;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assistant-chat__reply {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.assistant-chat__reply > p {
  margin: 0;
}

/* The contact's own messages, quoted as they wrote them. */
.assistant-chat__inbound {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.assistant-chat__inbound-bubble {
  min-width: 0;
  padding: 8px 12px;
  border-radius: 14px 14px 14px 4px;
  background: #f3f4f6;
}

.assistant-chat__inbound-text {
  margin: 0;
  color: #111827;
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-chat__inbound-time {
  display: block;
  margin-top: 2px;
  color: #9ca3af;
  font-size: 11px;
}

.assistant-chat__old-draft {
  padding: 10px 12px;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  background: #fafafa;
  color: #6b7280;
  font-size: 13px;
}

.assistant-chat__old-draft-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.assistant-chat__old-draft-subject {
  margin: 0 0 4px;
  font-weight: 600;
}

.assistant-chat__old-draft-text {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  white-space: pre-wrap;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

/* ── Drafted actions ─────────────────────────────────────── */
.assistant-chat__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.assistant-chat__actions-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #ede9fe;
  border-radius: 12px;
  background: linear-gradient(90deg, #f5f3ff, #eef2ff);
  color: #5b21b6;
  font-size: 13px;
  font-weight: 600;
}

.assistant-chat__actions-bar > span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.assistant-chat__actions-bulk {
  display: flex;
  gap: 8px;
}

/* ── Confirmation ────────────────────────────────────────── */
.assistant-chat__confirm {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 36px;
  padding: 14px;
  border: 1px solid #c4b5fd;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px -16px rgb(124 58 237 / 45%);
}

.assistant-chat__confirm-head {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6d28d9;
  font-size: 13px;
  font-weight: 600;
}

.assistant-chat__confirm-title {
  margin: 0;
  color: #111827;
  font-weight: 600;
}

.assistant-chat__confirm-summary {
  margin: 2px 0 0;
  color: #4b5563;
  font-size: 13px;
}

.assistant-chat__confirm-items {
  margin: 6px 0 0;
  padding-left: 18px;
  color: #374151;
  font-size: 13px;
}

.assistant-chat__confirm-meta {
  margin-left: 4px;
  color: #6b7280;
}

.assistant-chat__confirm-expired {
  margin: 0;
  color: #b45309;
  font-size: 13px;
}

.assistant-chat__confirm-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* ── Typing ──────────────────────────────────────────────── */
.assistant-chat__typing {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #eef0f4;
  border-radius: 4px 16px 16px 16px;
  background: #fff;
  color: #6b7280;
  font-size: 13px;
}

.assistant-chat__dots {
  display: inline-flex;
  gap: 4px;
}

.assistant-chat__dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #8b5cf6;
  animation: assistant-bounce 1.2s ease-in-out infinite;
}

.assistant-chat__dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.assistant-chat__dots i:nth-child(3) {
  animation-delay: 0.3s;
}

/* ── Composer ────────────────────────────────────────────── */
.assistant-chat__composer {
  flex-shrink: 0;
  padding: 10px 14px 12px;
  border-top: 1px solid #f0f0f5;
  background: rgb(255 255 255 / 85%);
  backdrop-filter: blur(6px);
}

.assistant-chat--roomy .assistant-chat__composer {
  padding: 12px 28px 16px;
}

.assistant-chat__input-wrap {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 6px 6px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.assistant-chat__input-wrap:focus-within {
  border-color: #a78bfa;
  box-shadow: 0 0 0 4px rgb(139 92 246 / 12%);
}

.assistant-chat__input-wrap.is-disabled {
  background: #f9fafb;
}

.assistant-chat__input {
  flex: 1;
  min-height: 24px;
  max-height: 140px;
  padding: 6px 0;
  border: none;
  outline: none;
  background: transparent;
  color: #111827;
  font: inherit;
  font-size: 14px;
  line-height: 1.45;
  resize: none;
}

.assistant-chat__input::placeholder {
  color: #9ca3af;
}

.assistant-chat__send {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 11px;
  background: linear-gradient(135deg, #7c3aed, #6366f1);
  box-shadow: 0 6px 14px -6px rgb(99 102 241 / 80%);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    opacity 0.15s ease;
}

.assistant-chat__send:hover:not(:disabled) {
  transform: translateY(-1px);
}

.assistant-chat__send:disabled {
  background: #e5e7eb;
  box-shadow: none;
  color: #9ca3af;
  cursor: not-allowed;
}

.assistant-chat__hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 8px 0 0;
  color: #9ca3af;
  font-size: 11px;
}

/* ── Motion ──────────────────────────────────────────────── */
@keyframes assistant-rise {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes assistant-bounce {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

@keyframes assistant-glow {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgb(139 92 246 / 45%);
  }
  50% {
    box-shadow: 0 0 0 6px rgb(139 92 246 / 0%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .assistant-chat__welcome,
  .assistant-chat__row,
  .assistant-chat__avatar--live,
  .assistant-chat__dots i {
    animation: none;
  }
}
</style>
